import { useContext, useState, useEffect, useCallback, useRef } from "react";
import { Routes, Route, useNavigate, Navigate, useLocation, Link } from "react-router-dom";
import { Toaster } from "sonner";
import LoginPage from "../features/auth/ui/Login/LoginPage";
import UserPage from "../widgets/UserPages";
import DueTodayStrip from "../features/crm/ui/DueTodayStrip";
import { EffectivePermissions, ImpersonatedEmployee, VerifyContext } from "./VerifyContext";
import { ThemeContext } from "./ThemeContext";
import { EmployeesProvider } from "../features/employees/modal/EmployeesContext";
import { FinanceProvider } from "../features/finance/modal/FinanceContext";
import { InventoryProvider } from "../features/inventory/modal/InventoryContext";
import { TeamsProvider } from "../features/teams/modal/teamsContext";
import {
  LayoutDashboard,
  Users,
  DollarSign,
  Package,
  Settings,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
  ClipboardList,
  Shield,
  FileText,
  BookOpen,
  UserCheck,
  Briefcase,
  Library,
  Contact,
  Sparkles,
  Landmark,
  Table2,
  NotebookText,
  Sun,
  Moon,
  CalendarDays,
} from "lucide-react";

// Exact routes and route prefixes whose page content has been migrated onto
// the token system — everything else still renders on the literal dark pin
// below until its own Phase 3 batch lands. Update this in the same PR that
// migrates a page's colors. Kept deliberately precise (not a blanket
// "/admin/finance" prefix) since Finance's category-lists and bank-accounts
// sub-pages are NOT migrated yet — a broad prefix would light-theme their
// still-hardcoded-dark content and make it unreadable.
const MIGRATED_EXACT_ROUTES = [
  "/admin/finance", "/admin/employees", "/admin/roles", "/admin/roles/employees",
  "/admin/inventory", "/admin/inventory/categories", "/admin/inventory/items",
  "/admin/teams",
  "/admin/cv-processor", "/admin/cv-processor/enhancements", "/admin/cv-processor/categories", "/admin/cv-processor/archived",
  "/employee/cv-processor", "/employee/cv-processor/enhancements", "/employee/cv-processor/categories", "/employee/cv-processor/archived",
  "/admin/attendance",
  "/admin/interview-qa", "/admin/interview-qa/categories", "/employee/interview-qa",
  "/admin/prompt-library", "/admin/prompt-library/categories", "/employee/prompt-library",
  "/resources",
  "/admin/crm", "/admin/crm/analytics", "/admin/crm/prime-contacts", "/admin/crm/prime-contacts/review-queue", "/admin/crm/companies", "/admin/crm/due-today", "/admin/crm/due-today/pipeline", "/admin/crm/due-today/conversations", "/admin/crm/due-today/performance",
  "/employee/crm", "/employee/crm/prime-contacts", "/employee/crm/prime-contacts/review-queue", "/employee/crm/companies", "/employee/crm/due-today", "/employee/crm/due-today/pipeline", "/employee/crm/due-today/conversations", "/employee/crm/due-today/performance",
  "/admin/sheets", "/employee/sheets",
  "/admin/documents", "/employee/documents",
  "/admin/job-board", "/employee/job-board",
  "/admin/hiring-contacts", "/employee/hiring-contacts",
  "/admin/cv-processor/cover-letters", "/employee/cv-processor/cover-letters",
  "/admin/settings",
  "/admin/meetings", "/employee/meetings",
  "/",
];
const MIGRATED_ROUTE_PREFIXES = [
  "/admin/finance/new-finance", "/admin/finance/update-finance",
  "/admin/employees/register-employees", "/admin/employees/update-employees", "/admin/employees/increament-history",
  "/admin/inventory/new-category", "/admin/inventory/update-category", "/admin/inventory/new-items", "/admin/inventory/update-items",
  "/admin/teams/new-team", "/admin/teams/update-team",
  "/admin/employee-evaluation",
];
// Matches routes with a dynamic segment in the middle (e.g. /admin/teams/:teamId/members)
// that a simple prefix/exact check can't express.
const MIGRATED_ROUTE_PATTERNS = [
  /^\/admin\/teams\/[^/]+\/members$/,
  /^\/admin\/sheets\/[^/]+$/,
  /^\/employee\/sheets\/[^/]+$/,
  /^\/admin\/documents\/[^/]+$/,
  /^\/employee\/documents\/[^/]+$/,
  /^\/admin\/crm\/companies\/[^/]+$/,
  /^\/employee\/crm\/companies\/[^/]+$/,
  /^\/admin\/crm\/prime-contacts\/[^/]+$/,
  /^\/employee\/crm\/prime-contacts\/[^/]+$/,
];
const isMigratedRoute = (pathname: string) =>
  MIGRATED_EXACT_ROUTES.includes(pathname) ||
  MIGRATED_ROUTE_PREFIXES.some((p) => pathname.startsWith(p)) ||
  MIGRATED_ROUTE_PATTERNS.some((re) => re.test(pathname));

function ImpersonationBanner({
  employee,
  onLeave,
}: {
  employee: ImpersonatedEmployee;
  onLeave: () => void;
}) {
  return (
    // Sits directly under the fixed 64px top bar and stays pinned while the
    // page scrolls (z-20: below the sidebar/top bar, above page content).
    <div className="sticky top-16 z-20 bg-warning-soft border-b border-warning-border px-5 lg:px-6 py-2 flex items-center justify-between gap-4 flex-shrink-0">
      <div className="flex items-center gap-2 text-warning-soft-foreground text-sm min-w-0">
        <UserCheck className="w-4 h-4 flex-shrink-0" />
        <span className="truncate">
          Viewing as <span className="font-semibold">{employee.name}</span>{" "}
          <span className="opacity-70">({employee.code})</span>
        </span>
      </div>
      <button
        onClick={onLeave}
        className="flex-shrink-0 flex items-center gap-1.5 text-xs font-medium text-warning-soft-foreground border border-warning-border hover:bg-warning/15 px-3 py-1.5 rounded-lg transition-colors"
      >
        <LogOut className="w-3.5 h-3.5" />
        Leave
      </button>
    </div>
  );
}

interface NavItem {
  label: string;
  icon: any;
  path: string;
  employeePath?: string;
  adminOnly: boolean;
  permissionPage?: string;
  customVisible?: boolean;
  children?: {
    label: string;
    path: string;
    employeePath?: string;
    adminOnly?: boolean;
    /** Extra check for highlighting this child on a route that isn't an exact match (e.g. a detail page nested under its list). Defaults to exact-path match. */
    isActive?: (pathname: string) => boolean;
  }[];
}

interface NavSection {
  label: string;
  items: NavItem[];
}

// The sidebar is grouped into labeled sections (Smartlead-style). Visibility,
// path resolution and active-state logic all operate on the flattened item
// list, so adding/moving an item between sections never touches that code.
const NAV_SECTIONS: NavSection[] = [
  {
    label: "Workspace",
    items: [
      { label: "Dashboard", icon: LayoutDashboard, path: "/", adminOnly: false },
      {
        label: "Sheets",
        icon: Table2,
        path: "/admin/sheets",
        employeePath: "/employee/sheets",
        adminOnly: false,
        permissionPage: "sheets",
      },
      {
        label: "Documents",
        icon: NotebookText,
        path: "/admin/documents",
        employeePath: "/employee/documents",
        adminOnly: false,
        permissionPage: "documents",
      },
    ],
  },
  {
    label: "People",
    items: [
      { label: "Employees", icon: Users, path: "/admin/employees", adminOnly: false, permissionPage: "employees" },
      { label: "Attendance", icon: ClipboardList, path: "/admin/attendance", adminOnly: false, permissionPage: "attendance" },
      { label: "Teams", icon: Users, path: "/admin/teams", adminOnly: false, permissionPage: "teams" },
      { label: "Employees Evaluation", icon: Users, path: "/admin/employee-evaluation", adminOnly: false, permissionPage: "evaluation" },
      {
        label: "Employee Meetings",
        icon: CalendarDays,
        path: "/admin/meetings",
        employeePath: "/employee/meetings",
        adminOnly: false,
        permissionPage: "meetings",
      },
      {
        label: "Roles",
        icon: Shield,
        path: "/admin/roles",
        adminOnly: true,
        children: [
          { label: "Manage Roles", path: "/admin/roles" },
          { label: "Role Employees", path: "/admin/roles/employees" },
        ],
      },
    ],
  },
  {
    label: "Operations",
    items: [
      {
        label: "Finance", icon: DollarSign, path: "/admin/finance", adminOnly: false, permissionPage: "finance",
        children: [
          { label: "Finance Listing", path: "/admin/finance" },
          { label: "Categories", path: "/admin/finance/category-lists" },
          { label: "Bank Accounts", path: "/admin/finance/bank-accounts" },
        ],
      },
      { label: "Inventory", icon: Package, path: "/admin/inventory", adminOnly: false, permissionPage: "inventory" },
    ],
  },
  {
    label: "Sales & Outreach",
    items: [
      {
        label: "SLED CRM",
        icon: Landmark,
        path: "/admin/crm",
        employeePath: "/employee/crm",
        adminOnly: false,
        permissionPage: "crm",
        children: [
          { label: "Pipeline", path: "/admin/crm", employeePath: "/employee/crm" },
          {
            label: "Prime Companies",
            path: "/admin/crm/companies",
            employeePath: "/employee/crm/companies",
            // Also highlight while viewing a single Company's detail page.
            isActive: (pathname) => pathname.endsWith("/crm/companies") || /\/crm\/companies\/\d+$/.test(pathname),
          },
          {
            label: "Prime Contacts",
            path: "/admin/crm/prime-contacts",
            employeePath: "/employee/crm/prime-contacts",
            // Also highlight while viewing a single Prime's detail page — numeric
            // id only, so this never collides with the sibling review-queue route below.
            isActive: (pathname) => pathname.endsWith("/crm/prime-contacts") || /\/crm\/prime-contacts\/\d+$/.test(pathname),
          },
          { label: "Email Review Queue", path: "/admin/crm/prime-contacts/review-queue", employeePath: "/employee/crm/prime-contacts/review-queue" },
          { label: "LinkedIn Outreach", path: "/admin/crm/due-today", employeePath: "/employee/crm/due-today" },
          { label: "Analytics", path: "/admin/crm/analytics", employeePath: "/employee/crm/analytics" },
        ],
      },
      {
        label: "Job Board",
        icon: Briefcase,
        path: "/admin/job-board",
        employeePath: "/employee/job-board",
        adminOnly: false,
        permissionPage: "job_board",
      },
      {
        label: "LinkedIn Hiring Contacts",
        icon: Contact,
        path: "/admin/hiring-contacts",
        employeePath: "/employee/hiring-contacts",
        adminOnly: false,
        permissionPage: "hiring_contacts",
      },
    ],
  },
  {
    label: "Knowledge",
    items: [
      {
        label: "CV Processor",
        icon: FileText,
        path: "/admin/cv-processor",
        employeePath: "/employee/cv-processor",
        adminOnly: false,
        children: [
          { label: "My CVs", path: "/admin/cv-processor", employeePath: "/employee/cv-processor" },
          { label: "My Cover Letters", path: "/admin/cv-processor/cover-letters", employeePath: "/employee/cv-processor/cover-letters" },
          { label: "Enhancements", path: "/admin/cv-processor/enhancements", employeePath: "/employee/cv-processor/enhancements" },
          { label: "Categories", path: "/admin/cv-processor/categories", employeePath: "/employee/cv-processor/categories" },
        ],
      },
      {
        label: "Interview Q&A",
        icon: BookOpen,
        path: "/admin/interview-qa",
        employeePath: "/employee/interview-qa",
        adminOnly: false,
        children: [
          { label: "Question Bank", path: "/admin/interview-qa", employeePath: "/employee/interview-qa" },
          { label: "Categories", path: "/admin/interview-qa/categories", adminOnly: true },
        ],
      },
      {
        label: "Prompt Library",
        icon: Sparkles,
        path: "/admin/prompt-library",
        employeePath: "/employee/prompt-library",
        adminOnly: false,
        permissionPage: "prompt_library",
        children: [
          { label: "Prompts", path: "/admin/prompt-library", employeePath: "/employee/prompt-library" },
          { label: "Categories", path: "/admin/prompt-library/categories", adminOnly: true },
        ],
      },
      {
        label: "Resources",
        icon: Library,
        path: "/resources",
        adminOnly: false,
      },
    ],
  },
];

// Fixed-chrome geometry shared by the top bar, sidebar and content column.
// z-index ladder (keep modals above all of this): top bar 40 · sidebar 30 ·
// mobile drawer backdrop 25 · impersonation banner 20 · in-page sticky ≤ 10 ·
// modals ≥ 50 (feature-local) / z-[999999] (shared Modal).
const SIDEBAR_WIDTH_CLASS = "w-60";          // 240px expanded
const SIDEBAR_RAIL_CLASS = "lg:w-[72px]";    // icon rail when collapsed
const CONTENT_PAD_EXPANDED = "lg:pl-60";
const CONTENT_PAD_COLLAPSED = "lg:pl-[72px]";

// The sidebar's collapse/expand toggle is desktop-only (the button that sets
// it is `hidden lg:inline-flex`), but the `collapsed` flag it produces is
// persisted to localStorage and would otherwise be read unconditionally on
// any viewport. This tracks the `lg` breakpoint (Tailwind's default, 1024px)
// so callers can gate `collapsed` behind it and never render a collapsed
// (icon-only, unlabeled) sidebar on mobile just because a desktop session
// left that preference saved.
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
}

function Sidebar({
  superAdmin,
  effectivePermissions,
  canAccessCvProcessor,
  canAccessInterviewQa,
  canAccessJobBoard,
  canAccessHiringContacts,
  canAccessPromptLibrary,
  canAccessCrm,
  canAccessSheets,
  canAccessDocuments,
  sidebarOpen,
  onClose,
  collapsed,
}: {
  superAdmin: boolean;
  effectivePermissions: EffectivePermissions;
  canAccessCvProcessor: boolean;
  canAccessInterviewQa: boolean;
  canAccessJobBoard: boolean;
  canAccessHiringContacts: boolean;
  canAccessPromptLibrary: boolean;
  canAccessCrm: boolean;
  canAccessSheets: boolean;
  canAccessDocuments: boolean;
  sidebarOpen: boolean;
  onClose: () => void;
  collapsed: boolean;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const effectiveCollapsed = collapsed && isDesktop;
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const isItemVisible = (item: NavItem) => {
    if (item.path === "/admin/cv-processor") return canAccessCvProcessor;
    if (item.path === "/admin/interview-qa") return canAccessInterviewQa;
    if (item.path === "/admin/job-board") return canAccessJobBoard;
    if (item.path === "/admin/hiring-contacts") return canAccessHiringContacts;
    if (item.path === "/admin/prompt-library") return canAccessPromptLibrary;
    if (item.path === "/admin/crm") return canAccessCrm;
    if (item.path === "/admin/sheets") return canAccessSheets;
    if (item.path === "/admin/documents") return canAccessDocuments;
    if (superAdmin) return true;
    if (item.adminOnly) return false;
    if (item.permissionPage) return !!effectivePermissions[item.permissionPage]?.can_view;
    return true;
  };

  const visibleSections = NAV_SECTIONS
    .map((section) => ({ ...section, items: section.items.filter(isItemVisible) }))
    .filter((section) => section.items.length > 0);
  const visibleItems = visibleSections.flatMap((section) => section.items);

  // Resolve the effective path for a nav item based on user type
  const resolvedPath = (path: string, employeePath?: string) =>
    !superAdmin && employeePath ? employeePath : path;

  // Auto-expand parent if a child route is active
  useEffect(() => {
    visibleItems.forEach((item) => {
      const activePath = resolvedPath(item.path, item.employeePath);
      if (item.children && location.pathname.startsWith(activePath)) {
        setExpandedItems((prev) => prev.includes(item.path) ? prev : [...prev, item.path]);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleExpand = (path: string) => {
    setExpandedItems((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  // Smartlead-style item states: soft brand tint + dark text + brand icon
  // when active (no solid pill, no trailing chevron); muted text at rest.
  const itemBase = `w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${effectiveCollapsed ? "lg:justify-center lg:px-0" : ""}`;
  const itemActive = "bg-sidebar-active text-sidebar-foreground font-medium [&>svg]:text-sidebar-primary";
  const itemIdle = "text-sidebar-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-surface";

  const renderItem = (item: NavItem) => {
    const effectiveItemPath = resolvedPath(item.path, item.employeePath);
    const isActive =
      effectiveItemPath === "/"
        ? location.pathname === "/"
        : location.pathname.startsWith(effectiveItemPath);
    const isExpanded = expandedItems.includes(item.path);

    if (item.children) {
      return (
        <div key={item.path}>
          <button
            onClick={() => {
              if (effectiveCollapsed) { navigate(effectiveItemPath); onClose(); }
              else toggleExpand(item.path);
            }}
            title={item.label}
            className={`${itemBase} ${isActive ? itemActive : itemIdle}`}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {!effectiveCollapsed && <span className="truncate">{item.label}</span>}
            {!effectiveCollapsed && (
              <ChevronDown className={`w-4 h-4 ml-auto shrink-0 text-sidebar-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`} />
            )}
          </button>
          {isExpanded && !effectiveCollapsed && (
            <div className="ml-[22px] mt-1 space-y-0.5 border-l border-sidebar-border pl-3">
              {item.children.filter((child) => !child.adminOnly || superAdmin).map((child) => {
                const effectiveChildPath = resolvedPath(child.path, child.employeePath);
                const isChildActive = child.isActive
                  ? child.isActive(location.pathname)
                  : location.pathname === effectiveChildPath;
                return (
                  <Link
                    key={child.path}
                    to={effectiveChildPath}
                    onClick={onClose}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] transition-colors ${
                      isChildActive
                        ? "bg-sidebar-active text-sidebar-primary font-medium"
                        : "text-sidebar-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-surface"
                    }`}
                  >
                    {child.label}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.path}
        to={effectiveItemPath}
        onClick={onClose}
        title={item.label}
        className={`${itemBase} ${isActive ? itemActive : itemIdle}`}
      >
        <item.icon className="w-5 h-5 flex-shrink-0" />
        {!effectiveCollapsed && <span className="truncate">{item.label}</span>}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile backdrop — starts under the fixed top bar so the bar stays
          tappable. z-[25]: above the sticky ImpersonationBanner (z-20) so the
          banner is dimmed and inert while the drawer is open, below the
          drawer itself (z-30). */}
      {sidebarOpen && (
        <div
          className="fixed inset-x-0 top-16 bottom-0 bg-overlay/50 z-[25] lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar — white panel pinned under the 64px top bar */}
      <aside
        className={`fixed top-16 bottom-0 left-0 z-30 ${SIDEBAR_WIDTH_CLASS} ${effectiveCollapsed ? SIDEBAR_RAIL_CLASS : "lg:w-60"} bg-sidebar border-r border-sidebar-border flex flex-col transition-[transform,width] duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <nav className={`flex-1 overflow-y-auto overflow-x-hidden py-3 ${effectiveCollapsed ? "px-2" : "px-3"}`}>
          {visibleSections.map((section, index) => (
            <div key={section.label} className={index > 0 ? "mt-2" : ""}>
              {effectiveCollapsed ? (
                index > 0 && <div className="my-2 mx-2 border-t border-sidebar-border" />
              ) : (
                <p className="px-3 pt-3 pb-1.5 text-[11px] font-medium uppercase tracking-wider text-sidebar-section select-none">
                  {section.label}
                </p>
              )}
              <div className="space-y-0.5">
                {section.items.map(renderItem)}
              </div>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}

function AccountMenu({ user, onLogout }: { user: { name: string; email: string }; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={user.name}
        className="w-9 h-9 rounded-full bg-topbar-foreground/15 ring-1 ring-topbar-foreground/20 flex items-center justify-center text-topbar-foreground text-sm font-semibold shrink-0 hover:bg-topbar-foreground/25 transition-colors"
      >
        {user.name?.charAt(0)?.toUpperCase() || "A"}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-surface border border-border rounded-xl shadow-xl z-20 overflow-hidden text-foreground">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
          </div>
          <Link
            to="/admin/settings"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-muted-foreground hover:bg-surface-muted hover:text-foreground transition-colors"
          >
            <Settings className="w-4 h-4" />
            Settings
          </Link>
          <button
            onClick={() => { setOpen(false); onLogout(); }}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-muted-foreground hover:bg-surface-muted hover:text-destructive transition-colors border-t border-border"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function TopBar({
  onMenuClick,
  collapsed,
  onToggleCollapse,
  user,
  onLogout,
}: {
  onMenuClick: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  user: { name: string; email: string };
  onLogout: () => void;
}) {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const isDesktop = useIsDesktop();
  return (
    // Full-width navy bar above both the sidebar and the content (Smartlead's
    // header). One leading button: collapses the rail on desktop, opens the
    // drawer on mobile.
    <header className="fixed top-0 inset-x-0 z-40 h-16 bg-topbar text-topbar-foreground flex items-center gap-3 px-4 lg:px-5">
      <button
        type="button"
        onClick={isDesktop ? onToggleCollapse : onMenuClick}
        title={isDesktop ? (collapsed ? "Expand sidebar" : "Collapse sidebar") : "Toggle navigation"}
        aria-label={isDesktop ? (collapsed ? "Expand sidebar" : "Collapse sidebar") : "Toggle navigation"}
        className="p-2 -ml-2 rounded-lg text-topbar-muted-foreground hover:text-topbar-foreground hover:bg-topbar-foreground/10 transition-colors"
      >
        {isDesktop
          ? (collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />)
          : <Menu className="w-5 h-5" />}
      </button>
      <Link to="/" className="flex items-center gap-2.5 min-w-0" title="Dashboard">
        <span className="w-8 h-8 rounded-lg bg-topbar-foreground/15 ring-1 ring-topbar-foreground/20 flex items-center justify-center text-base font-bold text-topbar-foreground shrink-0">
          H
        </span>
        <span className="text-lg font-semibold tracking-tight truncate">HRMS</span>
      </Link>
      <div className="ml-auto flex items-center gap-1.5">
        <button
          type="button"
          onClick={toggleTheme}
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          className="p-2 rounded-lg text-topbar-muted-foreground hover:text-topbar-foreground hover:bg-topbar-foreground/10 transition-colors"
        >
          {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
        <AccountMenu user={user} onLogout={onLogout} />
      </div>
    </header>
  );
}

export default function AppContent() {
  const {
    user,
    setUser,
    superAdmin,
    authCheckLoading,
    canAccessEmployees,
    canAccessEmployeeEvaluation,
    canAccessCvProcessor,
    canAccessInterviewQa,
    canAccessJobBoard,
    canAccessHiringContacts,
    canAccessPromptLibrary,
    canAccessCrm,
    canAccessSheets,
    canAccessDocuments,
    authUserType,
    effectivePermissions,
    impersonatedEmployee,
    leaveImpersonation,
  } = useContext(VerifyContext);
  const { theme } = useContext(ThemeContext);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem("hrms_sidebar_collapsed") === "1");
  const navigate = useNavigate();

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("hrms_sidebar_collapsed", next ? "1" : "0");
      return next;
    });
  }, []);

  const handleLogOut = useCallback(() => {
    const isImpersonating = !!localStorage.getItem("hrms_admin_token");
    localStorage.removeItem("token");
    localStorage.removeItem("authUserType");
    localStorage.removeItem("hrms_admin_token");
    localStorage.removeItem("hrms_impersonated_employee");
    setUser(null);
    navigate(authUserType === "admin" || isImpersonating ? "/admin/login" : "/employee/login");
  }, [setUser, navigate, authUserType]);

  const handleLeaveImpersonation = useCallback(async () => {
    const success = await leaveImpersonation();
    navigate(success ? "/" : "/admin/login");
  }, [leaveImpersonation, navigate]);

  // Close sidebar on route change (mobile)
  const location = useLocation();
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  if (authCheckLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <Toaster theme={theme === "dark" ? "dark" : "light"} richColors position="top-right" />
        <Routes>
          <Route path="/admin/login" element={<LoginPage />} />
          <Route path="/employee/login" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/employee/login" />} />
        </Routes>
      </>
    );
  }

  const appShell = (
    <div className="min-h-screen bg-background text-foreground">
      <Toaster
        theme={theme === "dark" ? "dark" : "light"}
        richColors
        position="top-right"
        toastOptions={{ classNames: { toast: "font-sans" } }}
      />
      <TopBar
        onMenuClick={() => setSidebarOpen((v) => !v)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebarCollapsed}
        user={user}
        onLogout={handleLogOut}
      />
      <Sidebar
        superAdmin={superAdmin}
        effectivePermissions={effectivePermissions}
        canAccessCvProcessor={canAccessCvProcessor}
        canAccessInterviewQa={canAccessInterviewQa}
        canAccessJobBoard={canAccessJobBoard}
        canAccessHiringContacts={canAccessHiringContacts}
        canAccessPromptLibrary={canAccessPromptLibrary}
        canAccessCrm={canAccessCrm}
        canAccessSheets={canAccessSheets}
        canAccessDocuments={canAccessDocuments}
        sidebarOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
      />

      {/* Content column — offset by the fixed top bar (pt-16) and sidebar (lg:pl-*) */}
      <div className={`pt-16 ${sidebarCollapsed ? CONTENT_PAD_COLLAPSED : CONTENT_PAD_EXPANDED} min-h-screen flex flex-col transition-[padding] duration-300`}>
        {impersonatedEmployee && (
          <ImpersonationBanner
            employee={impersonatedEmployee}
            onLeave={handleLeaveImpersonation}
          />
        )}

        {/*
          Most feature pages still use hardcoded dark-slate/white classes
          (Phase 3 migrates them one batch at a time) — pin this region's
          background/text to the pre-theming look, independent of the toggle,
          so unmigrated pages don't render illegible white-on-white in light
          mode. Migrated routes (isMigratedRoute above) opt out and get real
          token colors instead.
        */}
        <main className={`flex-1 p-5 lg:p-6 ${
          isMigratedRoute(location.pathname)
            ? "bg-background text-foreground"
            : "bg-slate-950 text-white"
        }`}>
          {/* LinkedIn due-today strip: undismissable, on every CRM page, only for users with CRM access */}
          <DueTodayStrip allowed={canAccessCrm} />
          <Routes>
            <Route
              path="/*"
              element={
                <UserPage
                  superAdmin={superAdmin}
                  canAccessEmployees={canAccessEmployees}
                  canAccessEmployeeEvaluation={canAccessEmployeeEvaluation}
                  name={user?.name || ""}
                />
              }
            />
          </Routes>
        </main>
      </div>
    </div>
  );

  return (
    <EmployeesProvider>
      <TeamsProvider>
        <InventoryProvider>
          <FinanceProvider>{appShell}</FinanceProvider>
        </InventoryProvider>
      </TeamsProvider>
    </EmployeesProvider>
  );
}
